/// <amd-module name="VSS/Features/Charts/Internal/AxisLabelFormatter" />

import { getShortMonth, shiftToUTC } from "./CompatUtilities";
import { getFriendlyDisplayValue } from "./FriendlyNumberFormatter";
import * as StringUtilities from "./StringUtilities";

/** Highcharts Label Formatter context...
 The interface below describes the information available to the formatter, when it is called by highcharts. */
export interface FormatterContext {
    /** The value of the data element */
    value: any;

    /** Indicates if the value is the first labelled element on the axis. */
    isFirst: boolean;
}

/** Presents a dual row representation of Day for marked ticks, and month (but only on the first occurence of each month).*/
export class AxisMonthFormatter {
    public previousRenderedMonthName: string;

    /**
     * Provides a formatter method for Highcharts to call into for obtaining axis label strings.
     */
    public getFormatter(): () => string {
        // We need highcharts to give us some context, which gets called as "this".
        // As a consequence, we can't use a lambda, and hold onto this.
        const that: AxisMonthFormatter = this;

        const handler = function() {
            // Now, we extract information from the context provided by highcharts.
            const thisState = this as FormatterContext;
            const isFirst = thisState.isFirst;
            let formattedText = thisState.value;

            // If the value was a number as expected, let's attempt to convert to a date and format it.
            if (typeof thisState.value == "number") {
                const dateInMs = thisState.value;
                formattedText = that.formatLabel(new Date(dateInMs), isFirst);
            }
            // Note: In the implicit else path, something is awry, and likely obvious to the developer.
            // Tracing out the problem would be obnoxious, as this codepath gets called repeatedly.
            return formattedText;
        };
        return handler;
    }

    /**
     * Generates a formatted label string for rendering.
     * For the first tick of each month, the month name is rendered.
     * For subsequent ticks in the same month, just the day number is rendered.
     *
     * @param date The date being described.
     * @param isFirst Indicates if this is the first label on the axis.
     */
    public formatLabel(date: Date, isFirst: boolean): string {
        const utcDate = shiftToUTC(date);
        const currentMonthName = getShortMonth(utcDate);
        const currentDay = utcDate.getDate();

        // Always label for the day of the month.
        let labelHtml: string = currentDay.toString();

        // Is this the first labelled day of this month? If so, provide the month name also.
        const showMonth = isFirst || this.previousRenderedMonthName != currentMonthName;
        if (showMonth) {
            this.previousRenderedMonthName = currentMonthName;

            labelHtml = labelHtml + "<br/>" + currentMonthName;
        }
        return labelHtml;
    }
}

/** Truncates labels to a maximum allowed length with ellipsis if necessary.*/
export class TruncationFormatter {
    public static MaxLabelLength = 15;

    /**
     * Provides a formatter method for Highcharts to call into for obtaining axis label strings.
     */
    public static getFormatter(suppressTruncateLabel: boolean = false): () => string {
        const handler = function() {
            let value = (this as FormatterContext).value;

            if (!suppressTruncateLabel) {
                value = StringUtilities.truncateString(value, TruncationFormatter.MaxLabelLength);
            }

            return value;
        };
        return handler;
    }
}

/**
 * Update the number into abbreviated format
 */
export class AbbreviatedNumberFormatter {
    public static getFormatter(): () => string {
        const handler = function() {
            let value = (this as FormatterContext).value;
            // If the value is not defined, then we need to get it from the point.
            if (value === undefined) {
                value = this.point && this.point.value;
            }
            if (!isNaN(value)) {
                return getFriendlyDisplayValue(value);
            } else {
                return value;
            }
        };
        return handler;
    }
}

/**
 * Formats numbers as percentages
 */
export class PercentageNumberFormatter {
    public static getFormatter(): () => string {
        const handler = function() {
            const value = (this as FormatterContext).value;
            if (value != null && !isNaN(value)) {
                return value + "%";
            } else {
                return value;
            }
        };
        return handler;
    }
}

/**
 * Format pivot table data with truncation of decimal values to a limit of two points.
 */
export class TabularLabelFormatter {
    public static getFormatter(): () => string {
        const handler = function() {
            const value = this.point.value;

            if (!isNaN(value)) {
                const fixedValue = +(value as Number).toFixed(2);
                return getFriendlyDisplayValue(fixedValue);
            } else {
                return "0";
            }
        };
        return handler;
    }
}

/**
 * Update the time span in seconds into hh:mm:ss format
 */
export class TimeSpanFormatter {
    private static formatToTwoDigits(time: string): string {
        if (time.length < 2) {
            time = "0" + time;
        }
        return time;
    }
    public static getFormatter(): () => string {
        const handler = function() {
            let value = (this as FormatterContext).value;
            // If the value is not defined, then we need to get it from the point.
            if (value === undefined) {
                value = this.point && this.point.value;
            }
            if (!isNaN(value)) {
                return TimeSpanFormatter.convertToTimeFormat(value);
            } else {
                return value;
            }
        };
        return handler;
    }

    public static convertToTimeFormat(totalSeconds: number): string {
        let hours;
        let minutes;
        let seconds;
        hours = TimeSpanFormatter.formatToTwoDigits(Math.floor(totalSeconds / 3600).toString());
        totalSeconds = totalSeconds % 3600;

        minutes = TimeSpanFormatter.formatToTwoDigits(Math.floor(totalSeconds / 60).toString());
        totalSeconds = totalSeconds % 60;

        seconds = TimeSpanFormatter.formatToTwoDigits(Math.floor(totalSeconds).toString());
        return hours + ":" + minutes + ":" + seconds;
    }
}
